import { cloneDeep } from 'lodash';
import { flushSync } from 'react-dom';
import React, { ChangeEvent, useState } from 'react';
import { MergeStockType } from '../IngredientEnums';
import { IngredientDocStockDTO, MergeIngredientDTO } from 'business/models/Models';
import { showAlertSnackBar } from 'utils/Global';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import EAlertType from 'enums/EAlertType';
import {
  MergeStocksButtonsProps,
  MergeStocksInputProps,
  MergeStocksProps,
  SetMergingStocksProps,
} from '../mergeIngredients/MergeIngredientConstants';
import { mergeIngredients } from 'business/services/IngredientService';
import {
  setIngTableDataIndicator,
  setIngredientSelectedRows,
} from 'redux/reducers/ingredientTableReducer';
import useVendorsList from '../hooks/useVendorsList';
import { ButtonsListContainer } from 'components/Button';

export const useGetInputFields = (props: MergeStocksInputProps) => {
  const {
    onClickAdd,
    tempStock,
    setTempStock,
    stock,
    onClickDelete,
    showButtons,
    currentMergeStockType,
    liquidClasses,
  } = props;
  if (!stock) {
    return;
  }
  const vendors = useVendorsList(true);
  const vendor = vendors.find((vendor) => vendor.id === stock.vendorId)?.name;
  const inputFields = {
    section1: [
      {
        tableHeading: 'Stock Number',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'stockNumber');
        },
        onClickRemoveBtn: (e) => {
          const resetStock = stock;
          resetStock.stockNumber = null;
          onClickDelete(cloneDeep(resetStock));
        },
        showButtons: showButtons,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.stockNumber = +e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.stockNumber,
        placeHolder: 'Stock Number',
        isRowSplit: false,
      },
      {
        tableHeading: 'Liquid Class',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'liquidClassId');
        },
        onClickRemoveBtn: (e) => {
          const resetStock = stock;
          resetStock.liquidClassId = null;
          onClickDelete(cloneDeep(resetStock));
        },
        showButtons: showButtons,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.liquidClassId = liquidClasses?.find(
              (liqClass) => liqClass.name === e.target.value,
            )?.id;
            setTempStock(tempStock);
          }
        },
        value: liquidClasses?.find((liqClass) => liqClass.id === stock.liquidClassId)
          ?.name, // liquidClass,
        placeHolder: 'Liquid Class',
        require: true,
      },
      {
        tableHeading: 'Low Conc.',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'lowConcentration');
        },
        onClickRemoveBtn: (e) => {
          const resetStock = stock;
          resetStock.lowConcentration = null;
          onClickDelete(cloneDeep(resetStock));
        },
        showButtons: showButtons,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.lowConcentration = +e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.lowConcentration,
        placeHolder: 'Low Conc.',
        require: true,
      },
      {
        tableHeading: 'High Conc.',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'highConcentration');
        },
        onClickRemoveBtn: (e) => {
          const resetStock = stock;
          resetStock.highConcentration = null;
          onClickDelete(cloneDeep(resetStock));
        },
        showButtons: showButtons,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.highConcentration = +e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.highConcentration,
        placeHolder: 'High Conc.',
        require: true,
      },
    ],
    section2: [
      {
        tableHeading: 'Vendor',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'vendorName');
        },
        onClickRemoveBtn: (e) => {
          const resetStock = stock;
          resetStock.vendorName = '';
          onClickDelete(cloneDeep(resetStock));
        },
        showButtons: showButtons,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.vendorName = e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.vendorName ? stock.vendorName : vendor,
        placeHolder: 'Vendor',
        require: true,
      },
      {
        tableHeading: 'Vendor Part Number',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'vendorPartNumber');
        },
        onClickRemoveBtn: (e) => {
          const resetStock = stock;
          resetStock.vendorPartNumber = '';
          onClickDelete(cloneDeep(resetStock));
        },
        showButtons: showButtons,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.vendorPartNumber = e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.vendorPartNumber,
        placeHolder: 'Vendor Part Number',
        require: true,
      },
    ],
    section3: [
      {
        tableHeading: 'Stock Conc.',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'stockConcentration');
        },
        showButtons: false,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.stockConcentration = +e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.stockConcentration,
        placeHolder: 'Stock Conc.',
        require: true,
      },
      {
        tableHeading: 'Conc. Unit',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'concentrationUnits');
        },
        showButtons: false,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.concentrationUnits = e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.concentrationUnits,
        placeHolder: 'Conc. Unit',
        require: true,
      },
      {
        tableHeading: 'pH',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'stockPh');
        },
        showButtons: false,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.stockPh = +e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.stockPh,
        placeHolder: 'pH',
        require: true,
      },
      {
        tableHeading: 'Prepared',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'isPrepared');
        },
        showButtons: false,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.isPrepared = Boolean(e.target.value);
            setTempStock(tempStock);
          }
        },
        value: stock.isPrepared,
        placeHolder: 'Prepared',
        require: true,
      },
    ],
    section4: [
      {
        tableHeading: 'Comments',
        onClickAddBtn: (e) => {
          onClickAdd(cloneDeep(tempStock), 'comments');
        },
        showButtons: false,
        onFocus: (e: ChangeEvent<HTMLInputElement>) => {
          if (currentMergeStockType !== MergeStockType.MergeStock) {
            const tempStock: IngredientDocStockDTO = new IngredientDocStockDTO();
            tempStock.comments = e.target.value;
            setTempStock(tempStock);
          }
        },
        value: stock.comments,
        placeHolder: 'Comments',
        require: true,
      },
    ],
  };

  return inputFields;
};
const contentContainerClass = 'setup-btn-outline-yellow-wide btn-outline-yellow-wide';
export const MergeStocksButtons: React.FC<MergeStocksButtonsProps> = (props) => {
  const {
    mergeStock,
    excludedId,
    excludedStockIds,
    mergeIngredient,
    matchedIngStocks,
    ingredients,
    stockIndex,
    ingTableDataIndicator,
    dispatch,
    setShowModal,
    setStocksShowModal,
    setMergeIngredient,
    setMergingStocks,
    setStocks,
    setMatchedStocks,
    setStockIndex,
    setExcludedStockIds,
    onClickMergeStock,
  } = props;

  const [isMerging, setIsMerging] = useState(false);

  const handleMergeClick = async () => {
    if (!validateStockInformation(mergeStock)) {
      return;
    }

    setIsMerging(true);
    setExcludedStockIds((prev) => [...prev, excludedId]);

    const stocks: MergeStocksProps = {
      mergeStock,
      excludedStockId: excludedId,
      setStocksShowModal,
      mergeIngredient,
      matchedIngStocks,
      setMergeIngredient,
      setMergingStocks,
      setStocks,
      ingredients,
      setMatchedStocks,
      setStockIndex,
      setShowModal,
      dispatch,
      ingTableDataIndicator,
      stockIndex,
      excludedStockIds: [...excludedStockIds, excludedId],
    };

    try {
      await onClickMergeStock(stocks, excludedId);
    } finally {
      setIsMerging(false);
    }
  };
  const closeStockModal = () => setStocksShowModal(false);
  const isDisabled = isMerging,
    showBorder = true;
  return (
    <ButtonsListContainer
      containerStyleClass="commands-container"
      buttonsList={[
        {
          title: 'Merge',
          onClick: handleMergeClick,
          contentContainerClass,
          isDisabled,
          showBorder,
        },
        {
          title: 'Cancel',
          onClick: closeStockModal,
          contentContainerClass,
          isDisabled,
          showBorder,
        },
      ]}
    />
  );
};

const validateStockInformation = (mergeStock: IngredientDocStockDTO) => {
  let error = '';
  if (mergeStock.stockNumber !== 0 && !mergeStock.stockNumber) {
    error = 'Type a stock number.';
  } else if (!mergeStock.stockConcentration) {
    error = 'Type the concentration.';
  } else if (!mergeStock.concentrationUnits) {
    error = 'Specify the concentration unit.';
  } else if (mergeStock.lowConcentration === null) {
    error = 'Type the low concentration.';
  } else if (mergeStock.highConcentration === null) {
    error = 'Type the high concentration.';
  } else if (mergeStock.lowConcentration < 0) {
    error = 'The low concentration must be a positive number.';
  } else if (mergeStock.highConcentration < 0) {
    error = 'The low concentration must be a positive number.';
  } else if (mergeStock.lowConcentration > mergeStock.stockConcentration) {
    error = 'The low concentration must be less than the resulting concentration.';
  } else if (mergeStock.highConcentration > mergeStock.stockConcentration) {
    error = 'Stock high concentration must be less than stock concentration.';
  } else if (mergeStock.lowConcentration > mergeStock.highConcentration) {
    error = 'The low concentration must be less than the high concentration.';
  }
  if (error !== '') {
    showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, error);
    return false;
  }
  return true;
};

export const setMergingStocks = async (props: SetMergingStocksProps) => {
  const {
    matchedIngStocks,
    mergedIngredient,
    stockIndex,
    setStocks,
    setMatchedStocks,
    setStockIndex,
    setStocksShowModal,
    ingredients,
    setShowModal,
    dispatch,
    ingTableDataIndicator,
    excludedStockIds,
    mergedAndDeletedStockIds,
  } = props;
  if (!matchedIngStocks) {
    return;
  }
  const keys: string[] = Object.keys(matchedIngStocks);
  if (stockIndex < keys.length && matchedIngStocks[keys[stockIndex]].length > 0) {
    const ingStockTwo: IngredientDocStockDTO = mergedIngredient.ingredientStock.find(
      (stock) => stock.id === parseInt(keys[stockIndex]),
    ) as IngredientDocStockDTO;
    const ingStockOne: IngredientDocStockDTO = matchedIngStocks[keys[stockIndex]][0];
    setStocks([ingStockOne, ingStockTwo]);
    const matchingStocks: IngredientDocStockDTO[] = matchedIngStocks[keys[stockIndex]];
    setMatchedStocks(matchingStocks);
    setStockIndex(stockIndex + 1);
    setStocksShowModal(true);
  } else {
    const excludedId: number =
      ingredients[0].id === mergedIngredient.id ? ingredients[1].id : ingredients[0].id;
    const mergeIngredientDto: MergeIngredientDTO = new MergeIngredientDTO({
      excludedIngredientId: excludedId,
      ingredient: mergedIngredient,
      isStockAlreadyMatched: true,
      excludedStockIds,
      mergedAndDeletedStockIds,
    });
    const resp = await mergeIngredients(mergeIngredientDto);
    if (resp === null) {
      dispatch(setIngredientSelectedRows([]));
      dispatch(setIngTableDataIndicator(ingTableDataIndicator + 1));
      setShowModal && setShowModal(false);
      setStocksShowModal(false);
      setStockIndex(0);
    }
  }
};

export const onClickMergeStock = async (props: MergeStocksProps) => {
  const {
    mergeStock,
    excludedStockId,
    setStocksShowModal,
    mergeIngredient,
    matchedIngStocks,
    setMergeIngredient,
    setStocks,
    ingredients,
    setMatchedStocks,
    setStockIndex,
    setShowModal,
    dispatch,
    ingTableDataIndicator,
    stockIndex,
    excludedStockIds,
    mergedAndDeletedStockIds,
    setIsMerging,
  } = props;
  setIsMerging(true);
  flushSync(() => {
    setStocksShowModal(false);
  });
  const ingredient = mergeIngredient;
  ingredient.ingredientStock = ingredient.ingredientStock
    ?.filter((stock) => stock.id !== excludedStockId)
    .map((stock) => {
      if (stock.id === mergeStock.id) {
        stock = mergeStock;
      }
      return stock;
    });

  const keys = Object.keys(matchedIngStocks);
  keys.map((key) => {
    const stocks = matchedIngStocks[key];
    matchedIngStocks[key] = stocks.filter((stock) => stock.id !== mergeStock.id);
    return matchedIngStocks[key];
  });

  setMergeIngredient(ingredient);

  const stockObj: SetMergingStocksProps = {
    matchedIngStocks: matchedIngStocks,
    ingredients: ingredients,
    mergedIngredient: mergeIngredient,
    stockIndex: stockIndex,
    setStocks: setStocks,
    setMatchedStocks: setMatchedStocks,
    setStockIndex: setStockIndex,
    setStocksShowModal: setStocksShowModal,
    setShowModal: setShowModal,
    dispatch: dispatch,
    ingTableDataIndicator: ingTableDataIndicator,
    excludedStockIds: excludedStockIds,
    mergedAndDeletedStockIds: mergedAndDeletedStockIds,
    setIsMerging,
  };
  try {
    await setMergingStocks(stockObj);
  } finally {
    setIsMerging(false);
  }
};
